import { useState, useEffect, useRef } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useSendTransaction } from 'wagmi';
import { parseEther, parseUnits, formatUnits } from 'viem';
import TokenSelector from './TokenSelector';
import { BACKEND_URL } from '../config/api';

// Minimal ERC-20 ABI — just what we need for the bridge transfer
const ERC20_TRANSFER_ABI = [
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to',     type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ type: 'bool' }],
  },
  {
    name: 'decimals',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint8' }],
  },
];

// Helper to safely convert values to BigInt, handling scientific notation and decimals
const safeBigInt = (value) => {
  if (!value) return BigInt(0);
  const strValue = String(value);
  // Check for scientific notation FIRST before splitting
  if (strValue.includes('e') || strValue.includes('E')) {
    return BigInt(Math.floor(parseFloat(strValue)));
  }
  // Remove any decimal points and everything after them
  const intValue = strValue.split('.')[0];
  return BigInt(intValue);
};

function BridgeSection() {
  const { address, isConnected } = useAccount();

  // Direction toggle
  const [direction, setDirection] = useState('xrpl-to-flare');

  // Shared
  const [selectedToken, setSelectedToken] = useState(null);
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // Config from backend
  const [config, setConfig] = useState({ flare_vault: null, xrpl_lockboxes: [] });

  // Bridge balance tracking
  const [bridgeBalance, setBridgeBalance] = useState(null);
  const [balanceRefetchTrigger, setBalanceRefetchTrigger] = useState(0);

  // XRPL → Flare: only need amount (Flare address = connected wallet)
  // Flare → XRPL: also need the XRPL destination address
  const [xrplDest, setXrplDest] = useState('');

  // Xaman signing payload (XRPL → Flare direction)
  // Persisted to sessionStorage so SSE can reconnect if the user navigates
  // away to Xaman and the page reloads when they return.
  const [xamanData, setXamanData] = useState(() => {
    try {
      const saved = sessionStorage.getItem('xaman_data');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  // SSE bridge status (shown after Xaman is opened)
  const [bridgeStatus, setBridgeStatus] = useState('');
  const [bridgeComplete, setBridgeComplete] = useState(false);
  const [feePaid, setFeePaid] = useState(false);
  // Track when a Flare → XRPL session is active (to trigger SSE connection)
  const [flareSessionActive, setFlareSessionActive] = useState(false);

  // wagmi hooks for Flare → XRPL direction
  const { writeContract, data: txHash, isPending, error: writeError } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash: txHash });

  // FLR fee payment (Flare → XRPL direction)
  const { sendTransaction, data: feeTxHash, isPending: isFeePending, error: feeError } = useSendTransaction();
  const { isSuccess: isFeeSuccess } = useWaitForTransactionReceipt({ hash: feeTxHash });
  // After FLR fee confirmed, trigger the ERC-20 transfer
  const [pendingErc20, setPendingErc20] = useState(null); // {address, amount}
  // Track if we've already triggered the ERC-20 transfer to prevent double-calls
  const erc20TriggeredRef = useRef(false);
  // Track if we're waiting for the user to sign the ERC-20 transfer
  const [waitingForErc20Signature, setWaitingForErc20Signature] = useState(false);
  // Manual trigger function for when auto-open fails
  const manualTriggerRef = useRef(null);

  // Fetch config on mount
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/config`)
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(err => console.error('Failed to fetch config:', err));
  }, []);

  // Fetch bridge balance when wallet or token changes (only for Flare → XRPL direction)
  useEffect(() => {
    if (!isConnected || !address || !selectedToken?.symbol || direction !== 'flare-to-xrpl') {
      setBridgeBalance(null);
      return;
    }

    fetch(`${BACKEND_URL}/api/bridge/balance/${address}/${selectedToken.symbol}`)
      .then(res => res.json())
      .then(data => {
        console.log('[Bridge] Balance fetched:', data);
        console.log('[Bridge] Token decimals from balance API:', data.decimals);
        console.log('[Bridge] Available to withdraw (formatted):', data.available_to_withdraw);

        // Backend now returns already-formatted values (not raw wei)
        // Just use them directly as numbers
        setBridgeBalance(data);
      })
      .catch(err => {
        console.error('Failed to fetch bridge balance:', err);
        setBridgeBalance(null);
      });
  }, [isConnected, address, selectedToken?.symbol, direction, balanceRefetchTrigger]);
  useEffect(() => {
    if (isFeeSuccess && pendingErc20 && config.flare_vault && !erc20TriggeredRef.current) {
      console.log('[Bridge] Fee payment confirmed, triggering ERC-20 transfer', {
        tokenContract: pendingErc20.address,
        vault: config.flare_vault,
        amount: pendingErc20.amount.toString(),
        tokenSymbol: selectedToken?.symbol,
        erc20TriggeredRef: erc20TriggeredRef.current,
      });

      // Mark as triggered immediately to prevent double-calls
      erc20TriggeredRef.current = true;

      // Clear pendingErc20 first to prevent re-runs
      const { address: tokenAddress, amount: tokenAmount } = pendingErc20;
      setPendingErc20(null);

      // Set waiting state IMMEDIATELY so mobile UI shows up right away
      setWaitingForErc20Signature(true);
      setStatus('Fee paid! Preparing token transfer...');

      // Create a manual trigger function that can be called if auto-trigger fails
      const doTransfer = () => {
        console.log('[Bridge] Calling writeContract for ERC-20 transfer');
        try {
          writeContract({
            address: tokenAddress,
            abi: ERC20_TRANSFER_ABI,
            functionName: 'transfer',
            args: [config.flare_vault, tokenAmount],
          });
          console.log('[Bridge] writeContract called - waiting for MetaMask popup');
        } catch (err) {
          console.error('[Bridge] Failed to trigger writeContract:', err);
          setStatus(`Failed to trigger wallet: ${err.message}`);
          setWaitingForErc20Signature(false);
          erc20TriggeredRef.current = false; // Reset on error so user can retry
        }
      };

      // Store the manual trigger function
      manualTriggerRef.current = doTransfer;

      // Add a delay to ensure fee transaction is fully processed
      // This is especially important on mobile where MetaMask needs time to close and reopen
      setTimeout(() => {
        setStatus(`Approve the w${selectedToken?.symbol || 'token'} transfer in your wallet...`);
        doTransfer();
      }, 1500);
    } else if (isFeeSuccess && pendingErc20 && !config.flare_vault) {
      console.error('[Bridge] Fee paid but flare_vault not configured!', { config });
      setStatus('Error: Bridge vault not configured. Please try again.');
      setPendingErc20(null);
    }
  }, [isFeeSuccess, pendingErc20, selectedToken?.symbol, config.flare_vault]);

  // Surface wallet errors in the status bar
  useEffect(() => {
    if (writeError) {
      console.error('[Bridge] Write error:', writeError);
      setStatus(`Wallet error: ${writeError.shortMessage || writeError.message}`);
      setWaitingForErc20Signature(false);
      erc20TriggeredRef.current = false; // Allow retry
    }
  }, [writeError]);
  useEffect(() => {
    if (feeError) {
      console.error('[Bridge] Fee error:', feeError);
      setStatus(`Fee payment error: ${feeError.shortMessage || feeError.message}`);
    }
  }, [feeError]);

  // Clear status and xaman panel when direction changes
  useEffect(() => {
    sessionStorage.removeItem('xaman_data');
    sessionStorage.removeItem('flare_bridge_pending'); // Clear Flare→XRPL pending state
    setStatus(''); setAmount(''); setXamanData(null);
    setBridgeStatus(''); setBridgeComplete(false); setFeePaid(false);
    setFlareSessionActive(false); // Reset Flare session flag
    erc20TriggeredRef.current = false; // Reset trigger flag
    setWaitingForErc20Signature(false);
    manualTriggerRef.current = null;
  }, [direction]);

  // Open SSE stream once we have a session ID so the UI gets live bridge updates.
  // Auto-reconnects on drop so we don't miss the completion event when the
  // browser suspends the tab while the user is in Xaman.
  // This hook now handles BOTH directions (XRPL → Flare and Flare → XRPL)
  useEffect(() => {
    const sid = sessionStorage.getItem('bridge_session_id');
    const dir = sessionStorage.getItem('bridge_direction');

    // For XRPL → Flare: wait until xamanData is set
    // For Flare → XRPL: wait until flareSessionActive is set
    if (!sid || !dir) return;
    if (dir === 'xrpl' && !xamanData) return;
    if (dir === 'flare' && !flareSessionActive) return;

    console.log(`[Bridge] Starting SSE listener for ${dir} → ${dir === 'xrpl' ? 'flare' : 'xrpl'} bridge`);

    if (dir === 'xrpl') {
      setBridgeStatus('Waiting for your XRPL payment to arrive...');
    } else if (dir === 'flare') {
      setStatus('Waiting for bridge completion...');
    }

    let es;
    let retryTimer;
    let done = false;

    const connect = () => {
      if (done) return;
      console.log(`[Bridge] Connecting to SSE stream: ${BACKEND_URL}/bridge/stream/${sid}`);
      es = new EventSource(`${BACKEND_URL}/bridge/stream/${sid}`);

      es.onopen = () => {
        console.log('[Bridge] SSE connection opened successfully');
      };

      es.onmessage = (e) => {
        console.log('[Bridge] SSE message received:', e.data);
        try {
          const msg = JSON.parse(e.data);
          if (msg.status === 'complete' || msg.event === 'complete') {
            done = true;
            clearTimeout(retryTimer);
            sessionStorage.removeItem('xaman_data');
            sessionStorage.removeItem('bridge_session_id');
            sessionStorage.removeItem('bridge_direction');

            if (dir === 'xrpl') {
              setBridgeStatus(`Bridge complete! ${amount} w${selectedToken?.symbol || 'tokens'} sent to your Flare wallet.`);
              setBridgeComplete(true);
              // Trigger bridge balance refetch so it updates when user switches to Flare → XRPL
              setBalanceRefetchTrigger(prev => prev + 1);
            } else if (dir === 'flare') {
              setStatus(`✅ Bridge complete! ${amount} ${selectedToken?.symbol || 'tokens'} sent to ${xrplDest}`);
              setLoading(false);
              setFlareSessionActive(false);
              // Trigger bridge balance refetch
              setBalanceRefetchTrigger(prev => prev + 1);
            }

            es.close();
          } else if (msg.status === 'fee_paid') {
            // Fee payment confirmed — enable Step 2
            setFeePaid(true);
            setBridgeStatus(msg.message || 'Fee payment confirmed! You can now send your tokens.');
          } else if (msg.status === 'error' || msg.event === 'error') {
            done = true;
            if (dir === 'xrpl') {
              setBridgeStatus(`Bridge error: ${msg.message || 'Unknown error'}`);
            } else if (dir === 'flare') {
              setStatus(`❌ Bridge error: ${msg.message || 'Unknown error'}`);
              setLoading(false);
              setFlareSessionActive(false);
            }
            es.close();
          } else if (msg.message) {
            if (dir === 'xrpl') {
              setBridgeStatus(msg.message);
            } else if (dir === 'flare') {
              setStatus(msg.message);
            }
          }
        } catch (err) {
          // Non-JSON frame — ignore
          console.log('[Bridge] Non-JSON SSE frame, ignoring');
        }
      };

      es.onerror = (err) => {
        console.log('[Bridge] SSE connection error, will retry in 3s');
        es.close();
        // Reconnect after 3 s — picks up buffered completion event if bridge
        // finished while the connection was down (e.g. tab in background).
        if (!done) retryTimer = setTimeout(connect, 3000);
      };
    };

    connect();
    return () => { done = true; clearTimeout(retryTimer); if (es) es.close(); };
  }, [xamanData, flareSessionActive]);

  // Polling fallback for fee_paid detection (mobile Xaman flow).
  // Primary: poll /api/bridge/session every 4 s (detects when XRPL listener set fee_paid_at).
  // Secondary: every 8 s also call /api/bridge/verify-fee with the XUMM payload UUID so the
  // backend can confirm the payment directly via XUMM — catches cases where the XRPL WebSocket
  // dropped while the user was in Xaman and the listener missed the incoming transaction.
  useEffect(() => {
    if (!xamanData || feePaid) return;
    const sid = sessionStorage.getItem('bridge_session_id');
    if (!sid) return;

    let pollCount = 0;

    const poll = async () => {
      try {
        // Primary check: session DB status (set by XRPL listener)
        const res = await fetch(`${BACKEND_URL}/api/bridge/session/${sid}`);
        if (res.ok) {
          const data = await res.json();
          if (data.fee_paid) {
            setFeePaid(true);
            setBridgeStatus('Fee payment confirmed! You can now send your tokens.');
            return;
          }
        }
      } catch { /* ignore network errors */ }

      // Secondary check (every other poll): verify via XUMM payload — catches cases where
      // the XRPL listener missed the payment due to a WebSocket reconnect.
      pollCount++;
      if (pollCount % 2 === 0 && xamanData.fee_uuid) {
        try {
          const vRes = await fetch(`${BACKEND_URL}/api/bridge/verify-fee`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sid, fee_uuid: xamanData.fee_uuid }),
          });
          if (vRes.ok) {
            const vData = await vRes.json();
            if (vData.fee_paid) {
              setFeePaid(true);
              setBridgeStatus('Fee payment confirmed! You can now send your tokens.');
            }
          }
        } catch { /* ignore network errors */ }
      }
    };

    poll(); // check immediately on mount / return from Xaman
    const interval = setInterval(poll, 4000);
    return () => clearInterval(interval);
  }, [xamanData, feePaid]);

  // Resume Flare → XRPL session after page reload (mobile fix)
  // On mobile, switching to MetaMask often causes page reload, losing React state
  useEffect(() => {
    const savedPending = sessionStorage.getItem('flare_bridge_pending');
    const savedSessionId = sessionStorage.getItem('bridge_session_id');
    const savedDirection = sessionStorage.getItem('bridge_direction');

    if (!savedPending || !savedSessionId || savedDirection !== 'flare') return;
    if (!config.flare_vault) return; // Wait for config to load

    try {
      const pending = JSON.parse(savedPending);
      console.log('[Bridge] 🔄 Resuming Flare→XRPL session after page reload:', {
        sessionId: savedSessionId,
        tokenSymbol: pending.tokenSymbol,
        amount: pending.amount,
      });

      // Check if fee was paid by looking at the transaction hash
      const checkFeeStatus = async () => {
        try {
          // If we have a fee transaction hash, check if it was confirmed on-chain
          if (pending.feeTxHash) {
            console.log('[Bridge] Checking if fee tx was confirmed:', pending.feeTxHash);

            // Use public RPC to check transaction receipt
            const rpcRes = await fetch(config.flare_vault ? `${BACKEND_URL}/api/rpc` : 'https://flare-api.flare.network/ext/C/rpc', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                jsonrpc: '2.0',
                id: 1,
                method: 'eth_getTransactionReceipt',
                params: [pending.feeTxHash],
              }),
            });

            const rpcData = await rpcRes.json();
            const receipt = rpcData.result;

            if (receipt && receipt.status === '0x1') {
              // Fee transaction confirmed successfully
              console.log('[Bridge] ✅ Fee transaction confirmed! Triggering ERC-20 transfer...');

              // Clear pending state before triggering to avoid loops
              sessionStorage.removeItem('flare_bridge_pending');

              // Restore pending ERC-20 data and trigger transfer
              setPendingErc20({
                address: pending.tokenContract,
                amount: BigInt(pending.amount),
              });

              setStatus(`Fee paid! Approve the w${pending.tokenSymbol} transfer in your wallet...`);
              setWaitingForErc20Signature(true);
              erc20TriggeredRef.current = false; // Allow trigger

              // Trigger the ERC-20 transfer
              setTimeout(() => {
                writeContract({
                  address: pending.tokenContract,
                  abi: ERC20_TRANSFER_ABI,
                  functionName: 'transfer',
                  args: [config.flare_vault, BigInt(pending.amount)],
                });
              }, 500); // Small delay to ensure state is set

              return; // Exit early
            } else if (receipt && receipt.status === '0x0') {
              // Fee transaction failed
              console.log('[Bridge] ❌ Fee transaction failed. Asking user to retry...');
              sessionStorage.removeItem('flare_bridge_pending');
              setStatus(`Fee payment failed. Please try again.`);
              return;
            }
          }

          // No fee tx hash or transaction not confirmed yet
          // Restore UI to show fee payment step
          console.log('[Bridge] Fee not paid yet. Restoring fee payment UI...');
          setPendingErc20({
            address: pending.tokenContract,
            amount: BigInt(pending.amount),
          });
          setStatus(`Step 1 of 2: Pay ${pending.feeFLR} FLR bridge fee — approve in your wallet...`);

          // Re-trigger fee payment
          sendTransaction({
            to: pending.treasuryFLR,
            value: parseEther(String(pending.feeFLR))
          });

        } catch (err) {
          console.error('[Bridge] Failed to check fee status:', err);
          setStatus('Error checking fee status. Please refresh and try again.');
        }
      };

      checkFeeStatus();

    } catch (err) {
      console.error('[Bridge] Failed to resume Flare→XRPL session:', err);
      sessionStorage.removeItem('flare_bridge_pending');
    }
  }, [config.flare_vault, writeContract, sendTransaction]); // Re-run if config loads

  // Track isPending state changes for debugging
  useEffect(() => {
    if (isPending) {
      console.log('[Bridge] ERC-20 transfer is now pending (isPending=true)');
      console.log('[Bridge] Transaction hash:', txHash);
    }
  }, [isPending, txHash]);

  // Track fee pending state changes for debugging
  useEffect(() => {
    if (isFeePending) {
      console.log('[Bridge] Fee payment is now pending (isFeePending=true)');
      console.log('[Bridge] Fee transaction hash:', feeTxHash);
    }
  }, [isFeePending, feeTxHash]);

  // Save fee transaction hash to sessionStorage when it's available
  useEffect(() => {
    if (feeTxHash) {
      console.log('[Bridge] Fee transaction hash received:', feeTxHash);
      const pending = sessionStorage.getItem('flare_bridge_pending');
      if (pending) {
        const data = JSON.parse(pending);
        data.feeTxHash = feeTxHash;
        sessionStorage.setItem('flare_bridge_pending', JSON.stringify(data));
        console.log('[Bridge] Saved fee tx hash to sessionStorage for resume capability');
      }
    }
  }, [feeTxHash]);

  // Confirm when tx lands
  useEffect(() => {
    if (isSuccess) {
      console.log('[Bridge] ERC-20 transfer confirmed on Flare');
      setStatus('Transfer confirmed on Flare. The bridge bot will deliver your $' + (selectedToken?.symbol || 'tokens') + ' on XRPL shortly.');
      setWaitingForErc20Signature(false);
      // Trigger bridge balance refetch so it updates after withdrawal
      setBalanceRefetchTrigger(prev => prev + 1);
    }
  }, [isSuccess, selectedToken?.symbol]);

  // ── XRPL → Flare ─────────────────────────────────────────────────────────
  // Creates a XUMM payload so Xaman pops up pre-filled. Falls back to manual
  // instructions if XUMM API keys aren't configured on the server.
  const handleXrplToFlare = async () => {
    if (!selectedToken) { setStatus('Please select a token.'); return; }
    if (!amount || parseFloat(amount) <= 0) { setStatus('Enter an amount.'); return; }
    if (!isConnected || !address) { setStatus('Connect your Flare wallet first.'); return; }

    setLoading(true);
    sessionStorage.removeItem('xaman_data');
    setXamanData(null);
    setFeePaid(false);
    setStatus('Creating Xaman signing request...');

    try {
      const res = await fetch(`${BACKEND_URL}/api/bridge/xaman-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token_symbol:  selectedToken.symbol,
          xrpl_issuer:   selectedToken.xrpl_issuer   || '',
          xrpl_currency: selectedToken.xrpl_currency || '',
          amount:        amount,  // Send as string to preserve precision for large integers
          flare_address: address,
        }),
      });

      // Surface server-side errors so we can see what went wrong
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setStatus(`Error: ${errData.detail || res.statusText}`);
        return;
      }

      const json = await res.json();
      sessionStorage.setItem('bridge_session_id', json.session_id);
      sessionStorage.setItem('bridge_direction', 'xrpl');

      if (json.xumm_available && json.sign_url) {
        sessionStorage.setItem('xaman_data', JSON.stringify(json));
        setXamanData(json);
        setStatus('');
      } else {
        // Fallback: XUMM keys not configured — show manual instructions
        const lockbox = json.lockbox_address || (config.xrpl_lockboxes && config.xrpl_lockboxes[0]) || 'Loading...';
        setStatus(
          `Send ${amount} $${selectedToken.symbol} on XRPL to:\n${lockbox}\n\n` +
          `Use this Memo (your Flare address):\n${address}\n\n` +
          `Waiting for your XRPL deposit...`
        );
      }
    } catch (e) {
      setStatus(`Request failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Flare → XRPL ─────────────────────────────────────────────────────────
  // Creates a session, then immediately triggers wallet to sign the ERC-20 transfer.
  const handleFlareToXrpl = async () => {
    if (!selectedToken) { setStatus('Please select a token.'); return; }
    if (!amount || parseFloat(amount) <= 0) { setStatus('Enter an amount.'); return; }
    if (!xrplDest.startsWith('r') || xrplDest.length < 25) {
      setStatus('Enter a valid XRPL address (starts with r).');
      return;
    }
    if (!isConnected || !address) { setStatus('Connect your Flare wallet first.'); return; }
    if (!selectedToken.flare_contract) {
      setStatus('This token does not have a Flare contract yet. Contact support.');
      return;
    }

    // Validate bridge balance: user can only withdraw what they've previously deposited
    // Backend returns already-formatted values, so compare directly
    if (bridgeBalance) {
      const userAmount = parseFloat(amount);
      const availableAmount = parseFloat(bridgeBalance.available_to_withdraw);

      if (userAmount > availableAmount) {
        setStatus(`Insufficient bridge balance. You can only withdraw up to ${availableAmount.toFixed(0)} ${selectedToken.symbol} that you've previously bridged from XRPL.`);
        return;
      }
    }

    setLoading(true);
    setStatus('Creating bridge session...');
    erc20TriggeredRef.current = false; // Reset trigger flag for new bridge session
    setWaitingForErc20Signature(false);
    manualTriggerRef.current = null;

    try {
      // IMPORTANT: On Flare, ALL wrapped tokens use 18 decimals, regardless of XRPL decimals
      // selectedToken.decimals represents XRPL decimals (0, 6, or 15) which is WRONG to use here
      // User enters user-friendly amounts (e.g., 1000000 = 1M tokens), so we must use parseUnits
      const decimals = 18;
      const amountStr = String(amount).trim();

      if (!amountStr || isNaN(parseFloat(amountStr))) {
        setStatus('Please enter a valid amount');
        setLoading(false);
        return;
      }

      const parsedAmount = parseUnits(amountStr, decimals);

      // Debug: Log the amount conversion
      console.log('[Bridge] Amount conversion:', {
        userInput: amount,
        amountStr: amountStr,
        decimalsUsed: decimals,
        parsedAmount: parsedAmount.toString(),
        parsedAmountHex: '0x' + parsedAmount.toString(16),
        note: 'User enters friendly amounts (e.g., 1000000 = 1M tokens), parseUnits converts to wei'
      });

      // Step 1: tell the backend so it watches for this transfer
      // Send parsedAmount (wei) as string to avoid precision loss for large integers
      const res = await fetch(`${BACKEND_URL}/bridge/start/flare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          xrpl_address: xrplDest,
          flare_address: address,
          amount: parsedAmount.toString(),  // Send wei amount as string
          token_symbol: selectedToken.symbol,
        }),
      });

      const json = await res.json();
      sessionStorage.setItem('bridge_session_id', json.session_id);
      sessionStorage.setItem('bridge_direction', 'flare');

      // Activate SSE listener for Flare → XRPL bridge
      setFlareSessionActive(true);

      // Step 2a: if a FLR fee + treasury are configured, collect fee first.
      // After the fee tx is confirmed the useEffect above fires the ERC-20 transfer.
      const treasuryFLR = json.fee_treasury_flr || '';
      const feeFLR      = json.fee_flr || 0;

      // Validate treasury address format
      const isValidTreasuryAddress = treasuryFLR && /^0x[a-fA-F0-9]{40}$/.test(treasuryFLR);

      if (feeFLR > 0 && treasuryFLR) {
        if (!isValidTreasuryAddress) {
          console.error('[Bridge] Invalid treasury address:', treasuryFLR);
          setStatus('Error: Bridge fee treasury address not configured properly. Please contact support.');
          setLoading(false);
          return;
        }

        console.log('[Bridge] Bridge fee required, initiating fee payment', {
          feeFLR,
          treasuryFLR,
          tokenContract: selectedToken.flare_contract,
          amount: parsedAmount.toString(),
          vaultAddress: config.flare_vault,
        });
        if (!config.flare_vault) {
          console.error('[Bridge] Vault address not loaded yet!');
          setStatus('Error: Bridge configuration not loaded. Please refresh and try again.');
          setLoading(false);
          return;
        }

        // Save pending state to sessionStorage so we can resume after page reload (common on mobile)
        sessionStorage.setItem('flare_bridge_pending', JSON.stringify({
          tokenContract: selectedToken.flare_contract,
          tokenSymbol: selectedToken.symbol,
          amount: parsedAmount.toString(),
          xrplDest: xrplDest,
          treasuryFLR: treasuryFLR,
          feeFLR: feeFLR,
        }));

        setPendingErc20({ address: selectedToken.flare_contract, amount: parsedAmount });
        setStatus(`Step 1 of 2: Pay ${feeFLR} FLR bridge fee — approve in your wallet...`);
        try {
          sendTransaction({ to: treasuryFLR, value: parseEther(String(feeFLR)) });
          console.log('[Bridge] Fee payment transaction initiated');
        } catch (err) {
          console.error('[Bridge] Failed to initiate fee payment:', err);
          setStatus(`Failed to start fee payment: ${err.message}`);
          setPendingErc20(null); // Clear pending state on error
          erc20TriggeredRef.current = false; // Reset trigger flag
        }
      } else {
        // No fee configured — go straight to ERC-20 transfer
        console.log('[Bridge] No fee required, initiating direct transfer', {
          tokenContract: selectedToken.flare_contract,
          vault: config.flare_vault,
          amount: parsedAmount.toString(),
        });
        setStatus(`Approve the w${selectedToken.symbol} transfer in your wallet...`);
        writeContract({
          address: selectedToken.flare_contract,
          abi: ERC20_TRANSFER_ABI,
          functionName: 'transfer',
          args: [config.flare_vault, parsedAmount],
        });
      }

    } catch (e) {
      setStatus('Failed to start bridge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isBusy = loading || isPending || isConfirming || isFeePending;

  const inputStyle = {
    width: '100%',
    padding: '12px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '1rem',
    boxSizing: 'border-box',
  };

  return (
    <div className="content-section active">
      <div className="section">

        {/* Direction toggle */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          <button
            onClick={() => setDirection('xrpl-to-flare')}
            style={{
              flex: 1, padding: '8px',
              backgroundColor: direction === 'xrpl-to-flare' ? '#FB923C' : '#2a2a2a',
              border: '1px solid #3a3a3a', borderRadius: '8px',
              cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px',
            }}
          >
            <img src="https://copper-causal-dove-26.mypinata.cloud/ipfs/bafkreihqxodkuh4ra2qhi4ypja22va7jd6en5gruvpahexmvrq6o5x2r6u" alt="XRPL → Flare" style={{ height: '44px', objectFit: 'contain', maxWidth: '100%' }} />
            <span style={{ fontSize: '11px', fontWeight: '700', color: direction === 'xrpl-to-flare' ? '#1a1a1a' : '#9ca3af', letterSpacing: '0.03em' }}>XRPL → Flare</span>
          </button>
          <button
            onClick={() => setDirection('flare-to-xrpl')}
            style={{
              flex: 1, padding: '8px',
              backgroundColor: direction === 'flare-to-xrpl' ? '#FB923C' : '#2a2a2a',
              border: '1px solid #3a3a3a', borderRadius: '8px',
              cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px',
            }}
          >
            <img src="https://copper-causal-dove-26.mypinata.cloud/ipfs/bafkreicdcnc7ylzulqb5nazsedrltcfiswszqfdf247an6u46stilriz2q" alt="Flare → XRPL" style={{ height: '44px', objectFit: 'contain', maxWidth: '100%' }} />
            <span style={{ fontSize: '11px', fontWeight: '700', color: direction === 'flare-to-xrpl' ? '#1a1a1a' : '#9ca3af', letterSpacing: '0.03em' }}>Flare → XRPL</span>
          </button>
        </div>

        {/* Token selector */}
        <TokenSelector
          selectedToken={selectedToken?.symbol || ''}
          onTokenChange={() => {}}
          label={direction === 'xrpl-to-flare' ? 'Token to bridge from XRPL' : 'Wrapped token to send back'}
          onFullTokenChange={setSelectedToken}
        />

        {/* Flare wallet auto-filled (XRPL → Flare) */}
        {direction === 'xrpl-to-flare' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: '#9ca3af', fontSize: '0.875rem' }}>
              You will receive on
            </label>
            <div style={{
              padding: '12px',
              backgroundColor: '#1a1a1a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              color: isConnected ? '#4ade80' : '#6b7280',
              wordBreak: 'break-all',
              overflowWrap: 'break-word',
            }}>
              {isConnected ? address : 'Connect your wallet above'}
            </div>
          </div>
        )}

        {/* XRPL destination (Flare → XRPL) */}
        {direction === 'flare-to-xrpl' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: '#9ca3af', fontSize: '0.875rem' }}>
              Your XRPL address to receive on
            </label>
            <input
              style={inputStyle}
              placeholder="r..."
              value={xrplDest}
              onChange={e => setXrplDest(e.target.value)}
            />
          </div>
        )}

        {/* Bridge Balance Display (Flare → XRPL only) */}
        {direction === 'flare-to-xrpl' && bridgeBalance && selectedToken && (() => {
          try {
            // Backend now returns already-formatted values (not raw wei)
            // Just display them directly without additional formatting
            const displayBalance = bridgeBalance.available_to_withdraw;
            const displayDeposited = bridgeBalance.deposited_amount;
            const displayWithdrawn = bridgeBalance.withdrawn_amount;

            return (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#1a1a2e',
                border: '1px solid #3b82f6',
                borderRadius: '8px',
              }}>
                <div style={{ color: '#93c5fd', fontSize: '0.75rem', marginBottom: '4px', fontWeight: '600' }}>
                  Bridge Balance
                </div>
                <div style={{ color: '#fff', fontSize: '1rem', fontWeight: '700', marginBottom: '6px' }}>
                  {parseFloat(displayBalance).toFixed(0)} {selectedToken.symbol}
                </div>
                <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>
                  You can only bridge back tokens you've previously bridged over from XRPL
                </div>
                {displayDeposited > 0 && (
                  <div style={{ color: '#6b7280', fontSize: '0.7rem', marginTop: '4px' }}>
                    Total deposited: {parseFloat(displayDeposited).toFixed(0)} •
                    Withdrawn: {parseFloat(displayWithdrawn).toFixed(0)}
                  </div>
                )}
              </div>
            );
          } catch (err) {
            console.error('[Bridge] Error formatting balance:', err, bridgeBalance);
            return (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#451a03',
                border: '1px solid #f97316',
                borderRadius: '8px',
              }}>
                <div style={{ color: '#fb923c', fontSize: '0.75rem' }}>
                  ⚠️ Error displaying balance. Please refresh and try again.
                </div>
              </div>
            );
          }
        })()}

        {/* Amount */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: '#9ca3af', fontSize: '0.875rem' }}>
            Amount
          </label>
          <div style={{ position: 'relative' }}>
            <input
              style={inputStyle}
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
            {/* Max button for Flare → XRPL direction */}
            {direction === 'flare-to-xrpl' && bridgeBalance && bridgeBalance.available_to_withdraw > 0 && (
              <button
                onClick={() => {
                  try {
                    // Backend returns already-formatted values, just use them directly
                    const maxAmount = bridgeBalance.available_to_withdraw;
                    // Round to whole number for cleaner display
                    setAmount(parseFloat(maxAmount).toFixed(0));
                  } catch (err) {
                    console.error('[Bridge] Error setting max amount:', err);
                    setStatus('Error calculating max amount. Please refresh and try again.');
                  }
                }}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: '6px 12px',
                  backgroundColor: '#FB923C',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#1a1a1a',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  letterSpacing: '0.05em',
                }}
              >
                MAX
              </button>
            )}
          </div>
          {/* Helper text for amount input */}
          {direction === 'flare-to-xrpl' && (
            <div style={{ marginTop: '6px', color: '#6b7280', fontSize: '0.75rem' }}>
              💡 Enter amount in standard format (e.g., 1000000 for 1M tokens, not wei)
            </div>
          )}
          {/* Warning if trying to withdraw more than available balance */}
          {direction === 'flare-to-xrpl' && bridgeBalance && amount && selectedToken && parseFloat(amount) > 0 && (() => {
            try {
              // Backend returns already-formatted values, compare directly
              const userAmount = parseFloat(amount);
              const availableAmount = parseFloat(bridgeBalance.available_to_withdraw);

              return userAmount > availableAmount && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px',
                  backgroundColor: '#451a03',
                  border: '1px solid #f97316',
                  borderRadius: '6px',
                  color: '#fb923c',
                  fontSize: '0.75rem',
                }}>
                  ⚠️ You can only withdraw up to {availableAmount.toFixed(0)} {selectedToken.symbol}
                </div>
              );
            } catch (err) {
              console.error('[Bridge] Error checking balance limit:', err);
              return null;
            }
          })()}
        </div>

        {/* Action button */}
        <button
          className="button"
          onClick={direction === 'xrpl-to-flare' ? handleXrplToFlare : handleFlareToXrpl}
          disabled={isBusy || !isConnected}
          style={{ width: '100%' }}
        >
          {!isConnected
            ? 'Connect Wallet to Bridge'
            : isFeePending
            ? 'Waiting for fee payment approval...'
            : isPending
            ? 'Waiting for token transfer approval...'
            : isConfirming
            ? 'Confirming on Flare...'
            : loading
            ? 'Starting bridge...'
            : direction === 'xrpl-to-flare'
            ? 'Start Bridge'
            : 'Send & Bridge Back'}
        </button>

        {/* Status */}
        {status && (
          <div className="status" style={{ whiteSpace: 'pre-line', marginTop: '16px' }}>
            {status}
          </div>
        )}

        {/* On mobile, writeContract doesn't auto-open the wallet — show a deep link */}
        {(isPending || isFeePending || waitingForErc20Signature) && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) && (
          <div>
            <a
              href="metamask://"
              onClick={() => {
                // Only manually trigger if not already pending (to avoid double-calling)
                if (waitingForErc20Signature && manualTriggerRef.current && !isPending) {
                  manualTriggerRef.current();
                }
              }}
              style={{
                display: 'block',
                marginTop: '12px',
                padding: '14px',
                backgroundColor: '#f6851b',
                color: '#fff',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1rem',
                textAlign: 'center',
              }}
            >
              {isFeePending
                ? 'Open MetaMask to pay fee'
                : waitingForErc20Signature
                ? `Open MetaMask to sign w${selectedToken?.symbol || 'token'} transfer`
                : `Open MetaMask to sign w${selectedToken?.symbol || 'token'} transfer`}
            </a>
            {(isPending || waitingForErc20Signature) && !isFeePending && (
              <div style={{
                marginTop: '8px',
                padding: '10px',
                backgroundColor: '#1a1a2e',
                border: '1px solid #3b82f6',
                borderRadius: '8px',
                color: '#93c5fd',
                fontSize: '0.85rem',
                textAlign: 'center',
              }}>
                ℹ️ If MetaMask doesn't show the transaction, tap the button above again or try closing and reopening MetaMask
              </div>
            )}
            {waitingForErc20Signature && manualTriggerRef.current && (
              <button
                onClick={() => {
                  if (manualTriggerRef.current) {
                    console.log('[Bridge] Manual retry triggered by user');
                    manualTriggerRef.current();
                  }
                }}
                style={{
                  width: '100%',
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #f6851b',
                  borderRadius: '8px',
                  color: '#f6851b',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                🔄 Retry Signing Request
              </button>
            )}
          </div>
        )}

        {/* Xaman signing panel (XRPL → Flare) */}
        {xamanData && (() => {
          const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
          // sign_url = https://xumm.app/sign/{uuid}
          // iOS/Android register this as an app link — tapping opens Xaman directly.
          // xumm:// is the fallback URI scheme for older installs.
          const httpsLink = xamanData.sign_url;
          const schemeLink = `xumm://sign/${xamanData.uuid}`;
          return (
            <div style={{
              marginTop: '20px',
              padding: '20px',
              backgroundColor: '#1a1a2e',
              border: '1px solid #0073cc',
              borderRadius: '12px',
              textAlign: 'center',
            }}>
              <div style={{ color: '#A78BFA', fontWeight: '700', fontSize: '1rem', marginBottom: '4px' }}>
                Sign in Xaman to send {amount} ${selectedToken?.symbol}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginBottom: '16px' }}>
                {isMobile ? 'Tap the button below to open Xaman' : 'Scan with Xaman or click the button'}
              </div>

              {/* XRP fee step — shown before the bridge payment if fee is configured */}
              {xamanData.fee_sign_url && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: feePaid ? '#14532d' : '#1a1a1a',
                  border: `1px solid ${feePaid ? '#4ade80' : '#8B5CF6'}`,
                  borderRadius: '8px',
                }}>
                  <div style={{ color: feePaid ? '#4ade80' : '#A78BFA', fontWeight: '600', fontSize: '0.85rem', marginBottom: '8px' }}>
                    {feePaid ? '✓ Step 1 of 2 — Fee Paid!' : `Step 1 of 2 — Pay ${xamanData.fee_xrp} XRP bridge fee`}
                  </div>
                  {!feePaid && (
                    <>
                      <a
                        href={xamanData.fee_sign_url}
                        style={{
                          display: 'block',
                          padding: '10px',
                          backgroundColor: '#7c3aed',
                          color: '#fff',
                          borderRadius: '8px',
                          textDecoration: 'none',
                          fontWeight: '700',
                          fontSize: '0.95rem',
                        }}
                      >
                        Pay fee in Xaman
                      </a>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '6px' }}>
                        After paying, Step 2 will unlock automatically
                      </div>
                    </>
                  )}
                  {feePaid && (
                    <div style={{ color: '#4ade80', fontSize: '0.75rem', marginTop: '4px' }}>
                      Fee received! Now send your ${selectedToken?.symbol} in Step 2 below
                    </div>
                  )}
                </div>
              )}

              {/* Bridge payment label */}
              {xamanData.fee_sign_url && (
                <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginBottom: '8px' }}>
                  Step 2 of 2 — Send {amount} ${selectedToken?.symbol}
                </div>
              )}

              {/* QR code — only useful on desktop */}
              {!isMobile && (
                <img
                  src={xamanData.qr_png}
                  alt="Scan with Xaman"
                  style={{
                    width: '180px',
                    height: '180px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    opacity: (xamanData.fee_sign_url && !feePaid) ? 0.3 : 1,
                    filter: (xamanData.fee_sign_url && !feePaid) ? 'grayscale(100%)' : 'none',
                  }}
                />
              )}

              {/* Primary: HTTPS universal link (iOS/Android app link) */}
              <div>
                <a
                  href={httpsLink}
                  onClick={(e) => {
                    // Prevent click if fee is required but not paid
                    if (xamanData.fee_sign_url && !feePaid) {
                      e.preventDefault();
                    }
                  }}
                  style={{
                    display: 'inline-block',
                    padding: '14px 36px',
                    backgroundColor: (xamanData.fee_sign_url && !feePaid) ? '#3a3a3a' : '#0073cc',
                    color: (xamanData.fee_sign_url && !feePaid) ? '#6b7280' : '#fff',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontWeight: '700',
                    fontSize: isMobile ? '1.1rem' : '0.95rem',
                    width: isMobile ? '100%' : 'auto',
                    boxSizing: 'border-box',
                    cursor: (xamanData.fee_sign_url && !feePaid) ? 'not-allowed' : 'pointer',
                    opacity: (xamanData.fee_sign_url && !feePaid) ? 0.5 : 1,
                  }}
                >
                  {(xamanData.fee_sign_url && !feePaid) ? '🔒 Complete Step 1 First' : 'Open in Xaman'}
                </a>
              </div>

              {/* Warning if trying to access Step 2 without paying fee */}
              {xamanData.fee_sign_url && !feePaid && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px',
                  backgroundColor: '#451a03',
                  border: '1px solid #f97316',
                  borderRadius: '6px',
                  color: '#fb923c',
                  fontSize: '0.75rem',
                }}>
                  ⚠️ You must pay the XRP bridge fee first before sending your tokens
                </div>
              )}

              {/* Secondary: URI scheme fallback (shown on mobile only) */}
              {isMobile && (
                <div style={{ marginTop: '10px' }}>
                  <a
                    href={schemeLink}
                    onClick={(e) => {
                      // Prevent click if fee is required but not paid
                      if (xamanData.fee_sign_url && !feePaid) {
                        e.preventDefault();
                      }
                    }}
                    style={{
                      display: 'inline-block',
                      padding: '10px 24px',
                      backgroundColor: 'transparent',
                      color: (xamanData.fee_sign_url && !feePaid) ? '#4b5563' : '#9ca3af',
                      border: '1px solid #3a3a3a',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      width: '100%',
                      boxSizing: 'border-box',
                      cursor: (xamanData.fee_sign_url && !feePaid) ? 'not-allowed' : 'pointer',
                      opacity: (xamanData.fee_sign_url && !feePaid) ? 0.5 : 1,
                    }}
                  >
                    Try alternative link
                  </a>
                </div>
              )}

              <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '12px' }}>
                This request expires in 10 minutes
              </div>

              {/* Live bridge status from SSE stream */}
              {bridgeStatus && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px',
                  backgroundColor: bridgeComplete ? '#14532d' : '#1e3a5f',
                  border: `1px solid ${bridgeComplete ? '#4ade80' : '#3b82f6'}`,
                  borderRadius: '8px',
                  color: bridgeComplete ? '#4ade80' : '#93c5fd',
                  fontSize: '0.85rem',
                }}>
                  {bridgeComplete ? '✓ ' : '⏳ '}{bridgeStatus}
                </div>
              )}

              <button
                onClick={() => {
                  sessionStorage.removeItem('xaman_data');
                  sessionStorage.removeItem('bridge_session_id');
                  sessionStorage.removeItem('bridge_direction');
                  setXamanData(null); setBridgeStatus(''); setBridgeComplete(false); setFeePaid(false);
                }}
                style={{
                  marginTop: '12px', background: 'none', border: 'none',
                  color: '#6b7280', cursor: 'pointer', fontSize: '0.8rem',
                }}
              >
                Cancel
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default BridgeSection;
